import React from "react";
import { Popover as RadixPopover } from "radix-ui";
import { useBoundedContainer } from "@/hooks/useBoundedContainer";
import { cn } from "@/utils/cn";

const Popover = RadixPopover.Root;
const PopoverTrigger = RadixPopover.Trigger;
const PopoverAnchor = RadixPopover.Anchor;

type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof RadixPopover.Content
> & {
  // Radix only exempts clicks on a registered Popover.Trigger from counting
  // as "outside" (via its internal triggerRef). A Popover.Anchor — needed
  // whenever the trigger must be a real editable input rather than a button —
  // never registers there, so clicking back into it while open gets misread
  // as an outside click: Radix closes the popover, the input's own
  // focus/click handler reopens it, and the round trip renders as a flicker.
  // Pass the anchor's ref here to exempt it, the same way a real Trigger is.
  ignoreInteractionsWithin?: React.RefObject<HTMLElement | null>;
  /** DOM id of the element to portal into, instead of `document.body`. Radix
   * positions with `strategy: "fixed"`, so the content stays viewport-anchored
   * either way — this only scopes where it mounts in the tree. */
  boundedContainerId?: string;
};

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      className = "",
      ignoreInteractionsWithin,
      boundedContainerId,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    const boundedContainer = useBoundedContainer(boundedContainerId);

    return (
      <RadixPopover.Portal container={boundedContainer ?? undefined}>
        <RadixPopover.Content
          ref={ref}
          onInteractOutside={(e) => {
            if (ignoreInteractionsWithin?.current?.contains(e.target as Node)) {
              e.preventDefault();
              return;
            }
            onInteractOutside?.(e);
          }}
          className={cn(
            "z-radixPopover motion-reduce:animate-none data-[state=open]:animate-popover-pop-in",
            className
          )}
          {...props}
        />
      </RadixPopover.Portal>
    );
  }
);
PopoverContent.displayName = "PopoverContent";

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
